import { toRaw } from 'vue'
import { until } from '@vueuse/core'
import { isSettled } from '@nicolaudie-group/ng-ui-kit/helpers/isStatus'
import routeNames from '@/router/names'
import pinia from '@/stores/pinia'
import useSupportStore from '@/stores/support.store'

/**
 * Hook called by the router (`SupportSession` route) when Regalade redirects the support
 * agent to the webapp to fulfill a profile request.
 *
 * It will simply store the profile request parameters in session storage so that they
 * are preserved accross refreshes, then redirect to the eventual provided goto parameter
 * or homepage.
 *
 * @param {Object} to - Route that triggered the hook (`SupportSession`)
 * @returns {Object} RouteLocation where the router must redirect (`goto` query)
 */
const redirectSupportSession = async (to) => {
  const supportStore = useSupportStore(pinia)

  // Join the support session
  supportStore.joinSession(to.params.requestId)

  // Wait for the support session data to be loaded
  await until(() => isSettled(supportStore.sessionStatus))
    .toBe(true, { timeout: 30000, throwOnTimeout: true })

  // Retrieve the loaded support session
  const supportSession = toRaw(supportStore.session)

  // In case no support session was loaded (error), return
  // undefined to stay on the shown error page
  if (!supportSession) return undefined

  // Otherwise, redirect to (in precedence order):
  // - the goto if provided
  // - the attached profile is applicable
  // - the homepage
  let goto = to.query?.goto
  if (!goto) {
    goto = supportSession.attachedProfileUid ? {
      name: routeNames.Profile,
      params: {
        profileUid: supportSession.attachedProfileUid,
      },
    } : { name: routeNames.Home }
  }
  return typeof goto === 'string' ? decodeURIComponent(goto) : goto
}

export default redirectSupportSession
